import { Box, Flex, Text } from '@chakra-ui/react';
import { vizDiv } from '../shared/viz'

export interface SensitivityListProps {
  /** Per-precinct ∂margin/∂effort values from the run (same data the desktop map colors). */
  sensitivities: { geoUnitId: string; value: number }[]
  /** Precinct polygons the page already fetches — used only for human-readable names. */
  features: { properties?: { id?: string | number; name?: string } | null }[]
  /** Max rows to show (top-N by value, highest first). */
  limit?: number
}

const fmt2 = (v: number) => v.toFixed(2)

/**
 * Mobile replacement for SensitivityMapPane: the same per-precinct sensitivity values as a ranked
 * list of horizontal bars (top-N by value), so MapLibre never loads on a phone. Precinct names are
 * joined from the page's precinct features; a missing name falls back to the id.
 */
export function SensitivityList({ sensitivities, features, limit = 15 }: SensitivityListProps) {
  const nameById = new Map<string, string>()
  for (const f of features) {
    const id = f.properties?.id
    if (id != null) nameById.set(String(id), f.properties?.name ?? `Precinct ${id}`)
  }

  const rows = sensitivities
    .slice()
    .sort((a, b) => b.value - a.value)
    .slice(0, limit)
  const max = rows.reduce((m, r) => Math.max(m, Math.abs(r.value)), 0)

  return (
    <Box bg="surface" p={6} borderRadius="lg" shadow="md">
      <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase">
        Where effort moves the needle
      </Text>
      <Text fontWeight="semibold" fontSize="sm" mb={3}>
        Sensitivity by precinct
      </Text>

      {rows.length === 0 ? (
        <Box bg="gray.50" borderRadius="md" px={4} py={6} textAlign="center">
          <Text fontSize="sm" fontWeight="semibold" color="gray.600">
            No sensitivity estimates for this contest yet
          </Text>
          <Text fontSize="xs" color="gray.500" mt={1}>
            They appear once precinct-level results are modeled for this race.
          </Text>
        </Box>
      ) : (
        rows.map((r) => {
          const pct = max > 0 ? (Math.abs(r.value) / max) * 100 : 0
          const positive = r.value >= 0
          const name = nameById.get(r.geoUnitId) ?? `Precinct ${r.geoUnitId}`
          return (
            <Flex key={r.geoUnitId} align="center" gap={2} mb={2}>
              <Text fontSize="xs" w="110px" flexShrink={0} lineClamp={1} title={name}>
                {name}
              </Text>
              <Box flex="1" h="14px" bg="gray.50" borderRadius="sm" overflow="hidden">
                <Box h="full" w={`${pct}%`} bg={positive ? vizDiv.pos : vizDiv.neg} borderRadius="sm" />
              </Box>
              <Text
                fontSize="xs"
                fontWeight="semibold"
                w="48px"
                textAlign="right"
                flexShrink={0}
                color="gray.700"
              >
                {fmt2(r.value)}
              </Text>
            </Flex>
          );
        })
      )}
    </Box>
  );
}
